import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Card, Chip, Skeleton, Breadcrumb, Button } from "@/ui";
import { manilaDateTime } from "@/lib/format";
import { Truck } from "lucide-react";

const STATUS_TONE = { paid: "good", done: "good", ready: "good", preparing: "warn", out_for_delivery: "warn", cod_unpaid: "warn", awaiting_payment: "warn", cancelled: "bad", refunded: "bad" };
const DELIVERY_STAGES = [
  { key: "placed", label: "Placed", statuses: ["cod_unpaid", "awaiting_payment", "paid"] },
  { key: "out_for_delivery", label: "Out for delivery", statuses: ["out_for_delivery"] },
  { key: "collected", label: "Collected", statuses: ["done"] },
];
function stageIndex(status) {
  if (status === "done") return 2;
  if (status === "out_for_delivery") return 1;
  return 0;
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const { shopId, shopName } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");

  const advance = async (stage) => {
    setBusy(stage); setError("");
    try {
      const res = await api.post(`/orders/${orderId}/delivery`, { shopId, stage });
      if (stage === "out_for_delivery") {
        setNotice(res.data?.customerTexted
          ? "We texted the customer that their order is on its way."
          : "Marked out for delivery. (No chat contact to text.)");
      }
      const r = await api.get(`/orders/${orderId}?shopId=${shopId}`);
      setOrder(r.data);
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(""); }
  };

  useEffect(() => {
    if (!shopId) { navigate("/pick-shop", { replace: true }); return; }
    setLoading(true);
    api.get(`/orders/${orderId}?shopId=${shopId}`).then((r) => setOrder(r.data)).catch((e) => setError(friendlyError(e))).finally(() => setLoading(false));
  }, [shopId, orderId, navigate]);

  return (
    <Layout shopName={shopName}>
      <div className="row" style={{ marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" data-testid="back-to-orders" onClick={() => navigate("/orders")}>
          <ArrowLeft size={20} strokeWidth={1.75} />back to orders
        </button>
      </div>
      {error ? <div className="err-text" data-testid="order-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      {loading ? (
        <div className="stack"><Skeleton variant="title" /><Skeleton variant="card" /></div>
      ) : order ? (
        <>
          <Breadcrumb items={[{ label: "orders" }, { label: `#${order.id.replace("order-", "")}` }]} />
          <div className="row" style={{ margin: "8px 0 24px" }}>
            <h1 className="t-h1">order #{order.id.replace("order-", "")}</h1>
            <Chip tone={STATUS_TONE[order.status] || "neutral"} dot>{order.statusLabel}</Chip>
            <Chip>{order.channelLabel}</Chip>
          </div>

          <Card className="section">
            <p className="muted-3 t-small">placed {manilaDateTime(order.placedAt)}</p>
            {order.customerName ? (
              <p className="t-body" style={{ marginTop: 4 }}>
                <strong>{order.customerName}</strong>{order.customerPhone ? <span className="muted num"> · {order.customerPhone}</span> : null}
              </p>
            ) : null}
          </Card>

          <div className="section">
            <h2 className="t-h2" style={{ marginBottom: 12 }}>what was bought</h2>
            <div className="tbl-wrap" data-testid="order-lines">
              <table className="tbl">
                <thead><tr><th>Item</th><th className="num">Each</th><th className="num">Qty</th><th className="num">Total</th></tr></thead>
                <tbody>
                  {order.lines.map((l, i) => (
                    <tr key={i}>
                      <td>{l.productName}{l.variantName ? <span className="muted"> ({l.variantName})</span> : null}</td>
                      <td className="num">{l.unitPrice}</td>
                      <td className="num">{l.qty}</td>
                      <td className="num">{l.lineTotal}</td>
                    </tr>
                  ))}
                  <tr><td colSpan={3} className="num"><strong>total</strong></td><td className="num" data-testid="order-total"><strong>{order.gross}</strong></td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {order.deliveryMethod === "delivery" && order.status !== "cancelled" && order.status !== "refunded" ? (
            <Card className="section" data-testid="delivery-card">
              <div className="row" style={{ gap: 8, marginBottom: 12 }}>
                <Truck size={18} strokeWidth={1.75} /><h2 className="t-h2">delivery</h2>
              </div>
              <p className="muted t-small" style={{ marginBottom: 12 }}>Track this cash-on-delivery order. Marking it collected records the payment.</p>
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                {DELIVERY_STAGES.map((st, i) => {
                  const cur = stageIndex(order.status);
                  const isDone = i <= cur;
                  const isNext = i === cur + 1;
                  return (
                    <Button key={st.key}
                      data-testid={`delivery-${st.key}`}
                      variant={isDone ? "primary" : "secondary"}
                      disabled={!isNext || !!busy}
                      loading={busy === st.key}
                      onClick={() => st.key !== "placed" && advance(st.key)}>
                      {i + 1}. {st.label}{isDone ? " \u2713" : ""}
                    </Button>
                  );
                })}
              </div>
              {notice ? <p className="t-small" data-testid="delivery-notice" style={{ marginTop: 12, color: "var(--good, #059669)" }}>{notice}</p> : null}
            </Card>
          ) : null}

          {order.canSeeMoney ? (
            <Card data-testid="order-money">
              <h2 className="t-h2" style={{ marginBottom: 12 }}>money breakdown</h2>
              <div className="row-between" style={{ marginBottom: 8 }}><span className="muted">total paid</span><span className="num">{order.gross}</span></div>
              <div className="row-between" style={{ marginBottom: 8 }}><span className="muted">our fee</span><span className="num">{order.platformFee}</span></div>
              <div className="row-between"><span><strong>you keep</strong></span><span className="num"><strong>{order.netToShop}</strong></span></div>
              <p className="muted-3 t-small" style={{ marginTop: 12 }}>these numbers were saved when the sale was made and never change.</p>
            </Card>
          ) : null}
        </>
      ) : null}
    </Layout>
  );
}
